import { useState, useEffect } from "react";
import Modal from "react-modal";
import { HelpCircle } from "lucide-react";
import api from '../../service/api';
import "../../css/Anamnese.css";
import "../../css/Lawton.css";

const PERGUNTAS_LAWTON = [
    { id: 0, texto: "1. Usar o telefone", tipo: "radio" },
    { id: 1, texto: "2. Ir a locais distantes (transporte)", tipo: "radio" },
    { id: 2, texto: "3. Fazer compras", tipo: "radio" },
    { id: 3, texto: "4. Preparar refeições", tipo: "radio" },
    { id: 4, texto: "5. Arrumar a casa", tipo: "radio" },
    { id: 5, texto: "6. Trabalhos manuais domésticos", tipo: "radio" },
    { id: 6, texto: "7. Lavar e passar roupa", tipo: "radio" },
    { id: 7, texto: "8. Tomar remédios (dose e horário)", tipo: "radio" },
    { id: 8, texto: "9. Cuidar das finanças", tipo: "radio" },
];

const OPCOES_LAWTON = [
    { id: 0, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 1, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 2, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 3, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 4, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 5, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 6, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 7, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
    { id: 8, opcoes: ["Sem ajuda", "Ajuda parcial", "Ajuda total"] },
];

// Mapeamento de pontos para o cálculo matemático
const MAPA_PONTOS_LAWTON = {
    "Sem ajuda": 3,
    "Ajuda parcial": 2,
    "Ajuda total": 1,
};

function Lawton({ setActiveTab, aluno, evaluationId, setEvaluationId, evaluationData, updateEvaluationData }) {
    const [etapa, setEtapa] = useState(1);
    const [isNextModal, setIsNextModal] = useState(false);
    const [showInfo, setShowInfo] = useState(false);

    const [respostasSelecionadas, setRespostasSelecionadas] = useState(
        PERGUNTAS_LAWTON.map((p) => ({ id: p.id, valor: "" }))
    );

    useEffect(() => {
        if (evaluationData?.lawton?.respostas) {
            setRespostasSelecionadas(evaluationData.lawton.respostas);
        }
    }, [evaluationData]);

    const totalEtapas = 2;
    const porcentagemConclusao = (etapa / totalEtapas) * 100;

    // --- LÓGICA DE AÇÃO ---

    const atualizarResposta = (perguntaId, novoValor) => {
        setRespostasSelecionadas((prev) =>
            prev.map((item) =>
                item.id === perguntaId ? { ...item, valor: novoValor } : item
            )
        );
    };

    const avancarEtapa = () => setEtapa((prev) => prev + 1);
    const retrocederEtapa = () => setEtapa((prev) => (prev > 1 ? prev - 1 : prev));

    const handleLawtonRegistrar = async () => {
        const payload = {
            lawton: { respostas: respostasSelecionadas },
            evaluationId
        };

        try {
            const response = await api.post(`/api/student/${aluno.id}/evaluation/lawton`, payload, { withCredentials: true });
            if (response.status === 200) {
                if (response.data.evaluationId && setEvaluationId) {
                    if (!evaluationId) {
                        if (!aluno.avaliacoesRealizadas) aluno.avaliacoesRealizadas = [];
                        aluno.avaliacoesRealizadas.push({
                            id: response.data.evaluationId,
                            data: new Date().toISOString()
                        });
                    }
                    setEvaluationId(response.data.evaluationId);
                }
                if (updateEvaluationData) {
                    updateEvaluationData({ lawton: payload.lawton });
                }
                setIsNextModal(true);
            }
        } catch (error) {
            console.error('Erro ao salvar Lawton:', error);
        }
    };

    // --- LÓGICA DE CÁLCULO ---

    const pontuacaoTotal = respostasSelecionadas.reduce(
        (acc, r) => acc + (MAPA_PONTOS_LAWTON[r.valor] || 0),
        0
    );

    const obterClassificacao = () => {
        if (respostasSelecionadas.some((r) => r.valor === ""))
            return "Selecione todas as alternativas";
        if (pontuacaoTotal >= 27) return "Independência Total";
        if (pontuacaoTotal >= 19) return "Dependência Parcial (Moderada)";
        return "Dependência Grave";
    };

    // --- RENDERIZAÇÃO ---

    const renderPerguntaLawton = (pergunta) => {
        const rObj = respostasSelecionadas.find((item) => item.id === pergunta.id);
        const valorAtual = rObj ? rObj.valor : "";
        const opcoesDaPergunta =
            OPCOES_LAWTON.find((opt) => opt.id === pergunta.id)?.opcoes || [];

        return (
            <div className="question-item" key={pergunta.id}>
                <div className="standard-title">{pergunta.texto}</div>
                <div className="answer-container">
                    {opcoesDaPergunta.map((opcao) => (
                        <label className="answer-item" key={opcao}>
                            <input
                                type="radio"
                                className="answer-radio"
                                name={`lawton-radio-${pergunta.id}`}
                                checked={valorAtual === opcao}
                                onChange={() => atualizarResposta(pergunta.id, opcao)}
                            />
                            <p className="answer-title">{opcao}</p>
                        </label>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="avaliacao-container">
            <div className="avaliacao-content">
                {/* ETAPA 1: Questionário (Dividido em duas colunas) */}
                {etapa === 1 && (
                    <div className="questions-container">
                        <div className="questions-left">
                            {PERGUNTAS_LAWTON.slice(0, 5).map(renderPerguntaLawton)}
                        </div>
                        <div className="questions-right">
                            {PERGUNTAS_LAWTON.slice(5, 9).map(renderPerguntaLawton)}
                        </div>
                    </div>
                )}

                {/* ETAPA 2: Resultado */}
                {etapa === 2 && (
                    <div className="questions-container">
                        <div className="questions-left">
                            <div className="question-title">Resumo do Desempenho</div>
                            <div className="indice-cap-func-quest">
                                <strong>Classificação: {obterClassificacao()}</strong> <br />
                                Pontuação Total: {pontuacaoTotal} / 27
                            </div>

                            <div className="interpretacao-cap-func-quest">
                                <p className={pontuacaoTotal >= 27 ? "risko-1" : ""}>
                                    27 Pontos: Independência Total
                                </p>
                                <p
                                    className={
                                        pontuacaoTotal < 27 && pontuacaoTotal >= 19 ? "risko-3" : ""
                                    }
                                >
                                    19-26 Pontos: Dependência Parcial
                                </p>
                                <p className={pontuacaoTotal < 19 ? "risko-5" : ""}>
                                    &lt; 19 Pontos: Dependência Grave
                                </p>
                            </div>
                        </div>

                        <div className="questions-right">
                            <div className="info-box-estatico">
                                <p>
                                    <strong>Dica:</strong> A pontuação máxima indica que o
                                    indivíduo é capaz de viver de forma independente na
                                    comunidade.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* FOOTER PADRONIZADO */}
                <div className="pagination-footer">
                    <div className="sub-pagination-footer">
                        <div className="sub-pagination-buttons">
                            <button
                                className="next-prev-button"
                                type="button"
                                onClick={retrocederEtapa}
                                disabled={etapa === 1}
                            >
                                Voltar
                            </button>
                            {etapa < totalEtapas ? (
                                <button
                                    className="next-prev-button"
                                    type="button"
                                    onClick={avancarEtapa}
                                >
                                    Avançar
                                </button>
                            ) : (
                                <button
                                    className="next-prev-button"
                                    type="button"
                                    onClick={handleLawtonRegistrar}
                                >
                                    Registrar
                                </button>
                            )}
                        </div>
                        <div className="progress-container">
                            <div
                                className="progress-bar"
                                style={{ width: `${porcentagemConclusao}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="info-box-container">
                        <div
                            className="info-button"
                            onMouseEnter={() => setShowInfo(true)}
                            onMouseLeave={() => setShowInfo(false)}
                        >
                            <HelpCircle size={20} /> Mais informações
                        </div>
                        {showInfo && (
                            <div className="info-box-content">
                                <p>
                                    A Escala de Lawton é um instrumento utilizado para avaliar a capacidade funcional do idoso na realização das Atividades Instrumentais da Vida Diária (AIVDs), como usar telefone, administrar medicações, preparar refeições, cuidar das finanças, realizar compras e deslocar-se com autonomia.
                                    <br/><br/>
                                    Sua aplicação permite identificar o nível de independência funcional em tarefas mais complexas do cotidiano, essenciais para a manutenção da vida autônoma na comunidade.
                                    <br/><br/>
                                    Essa avaliação é fundamental para detectar limitações precoces, orientar intervenções e auxiliar na promoção da autonomia, segurança e qualidade de vida do idoso.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <Modal
                    isOpen={isNextModal}
                    onRequestClose={() => setIsNextModal(false)}
                    contentLabel="Confirmacao"
                    className="modal-next"
                    overlayClassName="popup-overlay"
                >
                    <p className="popupNext-h1">Lawton finalizado!</p>
                    <p className="popupNext-h2">Deseja ir para a Escala de Berg?</p>
                    <div className="sub-pagination-buttons">
                        <button className="next-prev-button" type="button" onClick={() => setActiveTab("EscalaBeg")}>Sim</button>
                        <button className="next-prev-button" type="button" onClick={() => setIsNextModal(false)}>Não</button>
                    </div>
                </Modal>
            </div>
        </div>
    );
}

export default Lawton;